import { useEffect, useRef, useState } from 'react';
import { fileToBitmap, warpToRectangle, imageDataToBlob, rotateCanvas90 } from '../lib/document-scan';
import { orderCorners, defaultCorners, outputSizeFromCorners, capOutputSize, type Point } from '../lib/document-scan-geometry';
import CornerOverlay from './CornerOverlay';

// Deckelt die Ausgabe-Auflösung — hält die Perspektivkorrektur-Pixelschleife (siehe
// document-scan.ts) auch bei 12+ MP Kamerafotos schnell; ein signiertes Dokument
// braucht keine Foto-Auflösung, um lesbar zu bleiben.
const MAX_OUTPUT_DIM = 2200;

type Stage = 'loading' | 'adjusting' | 'processing' | 'preview';

interface Props {
  photoFile: File;
  onConfirm: (blob: Blob) => void;
  onRetake: () => void;
}

/** Orchestriert den Foto-Zuschnitt nach der Kamera-Aufnahme (Inkrement 3): Foto laden →
 *  Standard-Rechteck als Startpunkt (Kunde zieht die 4 Ecken per Ziehen zurecht, wie bei
 *  Adobe Scan — bewusst KEINE automatische Kantenerkennung mehr, siehe unten) →
 *  Perspektivkorrektur (reines Canvas/JS) → Vorschau mit Dreh-Button → Bestätigen liefert
 *  das fertige Bild. Schlägt das Laden/Verarbeiten fehl, fällt die Komponente auf das
 *  unbearbeitete Foto zurück — nie ein Blocker.
 *
 *  War ursprünglich mit OpenCV.js (WASM, automatische Kantenerkennung) gebaut; das
 *  ~15,5 MB große Modul blieb beim Initialisieren in Safari hängen (bestätigt: auch mit
 *  60s Timeout kein Erfolg — kein Netzwerk-/Geschwindigkeitsproblem, echte
 *  Inkompatibilität). Ersetzt durch reines JS ohne WASM-Abhängigkeit (Ticket 2026-07-22). */
export default function PhotoCorrection({ photoFile, onConfirm, onRetake }: Props) {
  const [stage, setStage] = useState<Stage>('loading');
  const [corners, setCorners] = useState<Point[] | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [fallback, setFallback] = useState(false);
  const bitmapRef = useRef<ImageBitmap | null>(null);

  useEffect(() => {
    let cancelled = false;
    const url = URL.createObjectURL(photoFile);
    setImageUrl(url);

    (async () => {
      try {
        const { bitmap, width, height } = await fileToBitmap(photoFile);
        if (cancelled) {
          bitmap.close();
          return;
        }
        bitmapRef.current = bitmap;
        setImageSize({ width, height });
        setCorners(orderCorners(defaultCorners(width, height)));
        setStage('adjusting');
      } catch (e) {
        if (!cancelled) {
          console.error('[PhotoCorrection] Foto konnte nicht geladen werden, Fallback:', e);
          setFallback(true);
          setPreviewUrl(url);
          setPreviewBlob(photoFile);
          setStage('preview');
        }
      }
    })();

    return () => {
      cancelled = true;
      URL.revokeObjectURL(url);
      bitmapRef.current?.close();
      bitmapRef.current = null;
    };
  }, [photoFile]);

  const confirmCorners = async () => {
    if (!corners || !bitmapRef.current) return;
    setStage('processing');
    try {
      const ordered = orderCorners(corners);
      const size = capOutputSize(outputSizeFromCorners(ordered), MAX_OUTPUT_DIM);
      const warped = warpToRectangle(bitmapRef.current, ordered, size);
      const blob = await imageDataToBlob(warped);
      setPreviewBlob(blob);
      setPreviewUrl(URL.createObjectURL(blob));
      setStage('preview');
    } catch (e) {
      console.error('[PhotoCorrection] Zuschneiden fehlgeschlagen, Fallback:', e);
      setFallback(true);
      setPreviewBlob(photoFile);
      setPreviewUrl(imageUrl);
      setStage('preview');
    }
  };

  const rotate = async () => {
    if (!previewBlob) return;
    const bitmap = await createImageBitmap(previewBlob);
    const canvas = rotateCanvas90(bitmap, 1);
    const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    if (!blob) return;
    setPreviewBlob(blob);
    setPreviewUrl(URL.createObjectURL(blob));
  };

  const confirm = () => {
    if (previewBlob) onConfirm(previewBlob);
  };

  if (stage === 'loading') {
    return <div className="text-sm text-slate-600 p-3">Foto wird geladen…</div>;
  }

  if (stage === 'adjusting' && corners && imageUrl && imageSize) {
    return (
      <div>
        <CornerOverlay imageUrl={imageUrl} imageWidth={imageSize.width} imageHeight={imageSize.height} corners={corners} onChange={setCorners} />
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={confirmCorners} className="flex-1 min-w-[8rem] px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium">
            Weiter
          </button>
          <button onClick={onRetake} className="flex-1 min-w-[8rem] px-4 py-2 border border-slate-300 rounded-lg text-sm">
            Erneut aufnehmen
          </button>
        </div>
      </div>
    );
  }

  if (stage === 'processing') {
    return <div className="text-sm text-slate-600 p-3">Wird zugeschnitten…</div>;
  }

  return (
    <div>
      {previewUrl && <img src={previewUrl} alt="Vorschau" className="max-w-full rounded-lg border border-slate-200" />}
      <div className="flex flex-wrap items-stretch gap-2 mt-3">
        <button onClick={rotate} className="shrink-0 px-3 py-2 border border-slate-300 rounded-lg text-sm">
          🔄 Drehen
        </button>
        <button onClick={confirm} className="flex-1 min-w-[8rem] px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium">
          Übernehmen
        </button>
        <button onClick={onRetake} className="flex-1 min-w-[8rem] px-4 py-2 border border-slate-300 rounded-lg text-sm whitespace-nowrap">
          Erneut aufnehmen
        </button>
      </div>
      {fallback && (
        <p className="text-xs text-slate-500 mt-2">Automatischer Zuschnitt nicht verfügbar — unbearbeitetes Foto wird verwendet.</p>
      )}
    </div>
  );
}
